import React, {useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import { User, LogonParams } from '../redux/slices/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { refreshAppAction, logout } from '../redux/slices/authSlice';


interface UserContextType {
    loggedInUser: User | null;
    authToken: string;
    logoutAction?: () => void;
    logonParams: LogonParams | null;
    refreshAction: () => Promise<void>;
    canLogin: boolean;
}

export const UserContext = React.createContext<UserContextType | null>(null);

const UserProvider = ({ children }: { children: ReactNode }) => {
    const { authState: { user, logonParams, tokens, loggedIn, canLogin, refreshing } } = useSelector((state: RootState) => state.auth);
    const userState = useSelector((state: RootState) => state.user);
    const accessToken = tokens.accessToken || "";
    const refreshToken = tokens.refreshToken ? tokens.refreshToken : sessionStorage.getItem("token") || "";
    const dispatch = useDispatch();

    // Initial check on component mount
    useEffect(() => {
        checkAndRefreshToken();
    }, []);

    // Check and refresh token whenever auth state changes
    useEffect(() => {
        checkAndRefreshToken();
    }, [loggedIn, refreshing]);

    // Function to check token and refresh if needed
    const checkAndRefreshToken = () => {
        const storedToken = sessionStorage.getItem("token");
        const hasToken = storedToken && storedToken.length > 0;
        
        // If not logged in but we have a token in storage, and we're not already refreshing
        if (!loggedIn && hasToken && !refreshing && !userState.optInLoginDialog.open) {
            refreshAction();
        }  else if (hasToken && (Object.keys(user || {}).length === 0)) {    // correction measure
            refreshAction();
        }
        // If no token in storage and not refreshing, ensure logged out state
        else if ((!refreshToken || refreshToken.length === 0) && !refreshing && loggedIn) {
            dispatch(logout());
        }
    };

    const refreshAction = async () => {
        try {
            dispatch(refreshAppAction(
                {
                    params: {
                        refreshToken: refreshToken,
                    },
                    method: {
                        url: '/auth/refresh-tokens',
                        action: 'POST',
                    },
                }
            ));
        } catch (err) {
            console.error("Error refreshing token:", err);
            dispatch(logout());
        }
    }

    const value = useMemo(() => ({ 
        loggedInUser: user || userState.user, 
        authToken: accessToken, 
        logonParams, 
        refreshAction, 
        canLogin,
        logoutAction: () => dispatch(logout())
    }), [user, accessToken, canLogin, logonParams]);
    
    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
      throw new Error('useUser must be used within a UserProvider');
    }
    return context;
  };

export default UserProvider;

